import { CartItem } from './hooks/useCart'
import { PaymentDetails } from './hooks/usePayments'

export type CartDictionary = Record<string, CartItem>

const toDictionary = (items: CartItem[] | CartDictionary) => {
  if (Array.isArray(items)) {
    const dictionary: CartDictionary = {}

    items.forEach((order) => {
      dictionary[order.id] = order
    })

    return dictionary
  }

  return items
}

const isPrimary = (order: CartItem) => !order.primaryId
const isSelected = (order: CartItem, selectedIds: string[]) =>
  selectedIds.includes(order.id)

const getPrimary = (order: CartItem, orders: CartItem[] | CartDictionary) => {
  if (!order.primaryId) {
    return undefined
  }
  const dictionary = toDictionary(orders)

  return dictionary[order.primaryId]
}

const getDependents = (order: CartItem, orders: CartItem[]) =>
  orders.filter((o) => o.primaryId === order.id)

const hasDependents = (order: CartItem, orders: CartItem[]) =>
  getDependents(order, orders).length > 0

const getItemInfo = (order: CartItem, orders: CartItem[]) => {
  const isPrimaryOrder = isPrimary(order)

  if (!isPrimaryOrder) {
    return {
      isPrimary: false,
      isDependent: true,
      hasDependents: false,
      dependents: [],
    }
  }
  const dependents = getDependents(order, orders)

  return {
    isPrimary,
    isDependent: false,
    hasDependents: dependents.length > 0,
    dependents,
  }
}

const isPaymentFinished = (payment: PaymentDetails | undefined) => {
  if (!payment) return false
  if (payment.method === 'terminal' || payment.method === 'paylink') {
    return payment.status === 'completed'
  }
  // We consider the payment process finished if it is paid, processing or created
  return (
    payment.status === 'completed' ||
    payment.status === 'waiting' ||
    payment.status === 'initialized'
  )
}

/**
 * Final status assumes the order is paid and the customer credits are updated
 *
 * For monthly/invoice payments, we consider the payment as final when the payment is created
 */
const isFinalStatus = (payment: PaymentDetails | undefined) => {
  if (!payment) return false
  if (payment.method === 'invoice' && payment.status === 'initialized') {
    return true
  }

  return payment.status === 'completed'
}

const CartUtils = {
  /** TODO: move this to PaymentUtils after we merge new store_stock changes */
  payments: {
    isFinished: isPaymentFinished,
    isFinalStatus,
  },
  isPrimary,
  isSelected,
  getPrimary,
  getDependents,
  hasDependents,
  getItemInfo,
  toDictionary,
}

export default CartUtils
